import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import SETTINGS from "../../globals";
import UserRoles from "../../model/enums/UserRole";
import PagedData from "../../model/PagedData";
import APIEndPoints from "../../utils/APIEndPoints";
import AppIcons from "../../utils/AppIcons";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import Loading from "../common/Loading";
import MyList from "../../model/MyList";
import MyListsListElement from "./MyListsListElement";

function MyListsList() {
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the navigator
  const navigate = useNavigate();
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // activities information to be displayed
  const [data, setData] = useState<MyList[]>([]);
  // manage data loading animation
  const [loading, setLoading] = useState(true);

  // load record data from the server
  useEffect(() => {
    apiClient
      ?.get<PagedData<MyList[]>>({
        url: APIEndPoints.MY_LISTS.ALL,
        requiresToken: true,
      })
      .then((data: PagedData<MyList[]>) => {
        setData(data.items);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [apiClient]);

  if (loading) {
    return <Loading />;
  }

  const canEdit =
    !EmbeddingToolInfo.isChemiTool() &&
    (authManager.isAdmin ||
      authManager.isModerator ||
      authManager.isSpecialist);

  return (
    <div>
      <div className="list-page-header">
        <div className="list-page-title">
          <h1>
            <span>{AppIcons.SITE}</span>
            My lists
          </h1>
        </div>
        <div>
          {canEdit && (
            <Button
              label={"Create a new list"}
              purpose={ButtonAction.CREATE}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.MY_LISTS,
                    subsection: AppSubSections.CREATE,
                  }),
                )
              }
            />
          )}
        </div>
      </div>
      {data?.length === 0 && (
        <div className="list-no-records">
          No records found. Change search parameters and try again.
        </div>
      )}
      {data?.length > 0 &&
        data.map((item) => {
          // check EDIT rights
          const canEdit =
            !EmbeddingToolInfo.isChemiTool() &&
            (item.id_user === authManager.getUserID() ||
              authManager.getUserRole() === UserRoles.ADMIN);

          return (
            <MyListsListElement canEdit={canEdit} data={item} key={item.id} />
          );
        })}
    </div>
  );
}

export default MyListsList;
